import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { completeLogin, startLogin } from './userManager';

/**
 * Landing route for the Cognito Hosted UI redirect. Completes the Authorization
 * Code + PKCE exchange, then replaces history with the page the user came from.
 */
export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes this effect in dev; signinRedirectCallback()
  // consumes one-time PKCE state, so a second call always throws.
  const started = useRef(false);

  useEffect(() => {
    let active = true;
    if (started.current) return;
    started.current = true;
    completeLogin()
      .then((returnTo) => {
        if (active) navigate(returnTo, { replace: true });
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Sign-in failed');
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  if (error) {
    return (
      <Box component="main" sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={2} alignItems="center" maxWidth={420}>
          <Alert severity="error" sx={{ width: '100%' }}>
            We couldn&apos;t complete sign-in: {error}
          </Alert>
          <Button variant="contained" onClick={() => void startLogin('/')}>
            Try signing in again
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress aria-label="Completing sign-in" />
        <Typography color="text.secondary">Signing you in…</Typography>
      </Stack>
    </Box>
  );
};
